import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { FULL_SCREEN_MARGIN, ProcessStep, SLIDE_HEIGHT, SLIDE_WIDTH } from "../constants";
import {
    getCurrentTemplate, getNotes,
    getPresentationId,
    getProcessStep,
    getSlides,
    getTemplateId,
    getTextFieldValue,
    Presentation,
    State
} from "../reducers";
import {
    createSlides,
    deleteAllSlides,
    updateProcessStep,
    updateTemplateId,
    updateTextField,
    EmptySlide,
} from "../services/presentation-store";
import { Undoable } from "./undo";
import { getTemplate } from "../services/template-store";
import { reportError, ReportErrorAction } from "./ui-state";
import { setupSlides } from "../services/slide_helper";

export interface SetPresentationAction {
    readonly type: 'SET_PRESENTATION',
    readonly presentation: Presentation,
    readonly maxZoomLevel: number,
}

export interface SetupStoryboardAction extends Undoable {
    readonly type: 'SETUP_STORYBOARD',
    readonly slides: ReadonlyArray<EmptySlide>,
}

export interface SetProcessStepAction extends Undoable {
    readonly type: 'SET_PROCESS_STEP',
    readonly oldProcessStep: ProcessStep,
    readonly newProcessStep: ProcessStep,
}

export interface SetTemplateIdAction extends Undoable {
    readonly type: 'SET_TEMPLATE_ID',
    readonly oldTemplateId: number,
    readonly newTemplateId: number,
    readonly canvasWidthRatio: number,
    readonly canvasHeightRatio: number,
    readonly storyboardWidthRatio: number,
    readonly storyboardHeightRatio: number,
    readonly oldMaxZoomLevel: number,
    readonly newMaxZoomLevel: number,
}

export interface SetTextFieldAction extends Undoable {
    readonly type: 'SET_TEXT_FIELD',
    readonly key: string,
    readonly oldValue: string,
    readonly newValue: string,
}

export function setPresentation(presentation: Presentation): SetPresentationAction {
    const { storyboardWidth, storyboardHeight } = getTemplate(presentation.templateId);
    const marginFactor = (1 + 2 * FULL_SCREEN_MARGIN);
    const maxZoomLevel = Math.min(storyboardWidth / marginFactor / SLIDE_WIDTH, storyboardHeight / marginFactor / SLIDE_HEIGHT);
    return {
        type: 'SET_PRESENTATION',
        presentation,
        maxZoomLevel,
    }
}

export function setProcessStep(newProcessStep: ProcessStep): ThunkAction<any, State, any, SetProcessStepAction | SetupStoryboardAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, SetProcessStepAction | SetupStoryboardAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const presentationId = getPresentationId(state);
        if (newProcessStep !== ProcessStep.SKETCH || getSlides(state).length > 0) {
            const oldProcessStep = getProcessStep(state);
            const redoEffect = async () => await updateProcessStep(presentationId, newProcessStep)
                .catch(error => dispatch(reportError(error)));
            const undoEffect = async () => await updateProcessStep(presentationId, oldProcessStep)
                .catch(error => dispatch(reportError(error)));
            dispatch({
                type: 'SET_PROCESS_STEP',
                oldProcessStep,
                newProcessStep,
                redoEffect,
                undoEffect,
            });
            await redoEffect();
        } else {
            const template = getCurrentTemplate(state);
            const notes = getNotes(state);
            const slides = setupSlides(template, notes);
            const redoEffect = async () =>
                await updateProcessStep(presentationId, ProcessStep.SKETCH)
                    .then(() => createSlides(presentationId, slides))
                    .catch(error => dispatch(reportError(error)));
            const undoEffect = async () =>
                await updateProcessStep(presentationId, ProcessStep.STRUCTURE)
                    .then(() => deleteAllSlides(presentationId))
                    .catch(error => dispatch(reportError(error)));
            dispatch({
                type: 'SETUP_STORYBOARD',
                slides,
                redoEffect,
                undoEffect,
            });
            await redoEffect();
        }
    };
}

export function setTemplate(newTemplateId: number): ThunkAction<any, State, any, SetTemplateIdAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, SetTemplateIdAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const presentationId = getPresentationId(state);
        const oldTemplateId = getTemplateId(state);
        const oldTemplate = getCurrentTemplate(state);
        const newTemplate = getTemplate(newTemplateId);
        const canvasWidthRatio = newTemplate.canvasWidth / oldTemplate.canvasWidth;
        const canvasHeightRatio = newTemplate.canvasHeight / oldTemplate.canvasHeight;
        const storyboardWidthRatio = newTemplate.storyboardWidth / oldTemplate.storyboardWidth;
        const storyboardHeightRatio = newTemplate.storyboardHeight / oldTemplate.storyboardHeight;
        const marginFactor = (1 + 2 * FULL_SCREEN_MARGIN);
        const oldMaxZoomLevel = Math.min(oldTemplate.storyboardWidth / marginFactor / SLIDE_WIDTH, oldTemplate.storyboardHeight / marginFactor / SLIDE_HEIGHT);
        const newMaxZoomLevel = Math.min(newTemplate.storyboardWidth / marginFactor / SLIDE_WIDTH, newTemplate.storyboardHeight / marginFactor / SLIDE_HEIGHT);
        const redoEffect = async () => await updateTemplateId(presentationId, newTemplateId)
            .catch(error => dispatch(reportError(error)));
        const undoEffect = async () => await updateTemplateId(presentationId, oldTemplateId)
            .catch(error => dispatch(reportError(error)));
        dispatch({
            type: 'SET_TEMPLATE_ID',
            oldTemplateId,
            newTemplateId,
            canvasWidthRatio,
            canvasHeightRatio,
            storyboardWidthRatio,
            storyboardHeightRatio,
            oldMaxZoomLevel,
            newMaxZoomLevel,
            redoEffect,
            undoEffect,
        });
        await redoEffect();
    };
}

export function setTextField(key: string, newValue: string): ThunkAction<any, State, any, SetTextFieldAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, SetTextFieldAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const presentationId = getPresentationId(state);
        const oldValue = getTextFieldValue(state, key);
        const redoEffect = async () => await updateTextField(presentationId, key, newValue)
            .catch(error => dispatch(reportError(error)));
        const undoEffect = async () => await updateTextField(presentationId, key, oldValue)
            .catch(error => dispatch(reportError(error)));
        dispatch({
            type: 'SET_TEXT_FIELD',
            key,
            oldValue,
            newValue,
            redoEffect,
            undoEffect
        });
        await redoEffect();
    }
}



// WEBPACK FOOTER //
// ./src/actions/presentation.ts